import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { toast } from '@/hooks/use-toast';
import { useRealtimeSync } from '@/hooks/useRealtimeSync';
import type { WorkflowState, MultiSheetWorkflowState } from '@/types/workflow';

interface UseCoopWorkspaceDeps {
  /** Logged-in user (for presence + RLS). Null when anonymous. */
  user: User | null;
  isAuthenticated: boolean;
  /** The workflow id currently loaded (null = fresh workspace). */
  currentDocumentId: string | null;
  /** Current editable workflow entities — any change triggers a delta push. */
  blocks: unknown;
  connectors: unknown;
  groups: unknown;
  /** Build the active-sheet snapshot for broadcast. */
  getActiveSheetState: () => WorkflowState;
  /** Apply an incoming remote state. */
  loadState: (state: WorkflowState | MultiSheetWorkflowState) => void;
  /** Reset layer-navigation after a join. */
  resetLayerPath: () => void;
  /** Set currentDocumentId once the coop workflow has been loaded. */
  setCurrentDocumentId: (id: string | null) => void;
  /** Load a workflow the caller may access as owner OR coop member. */
  loadCoopWorkflow: (id: string) => Promise<WorkflowState | MultiSheetWorkflowState | null>;
  /** Translator for user-facing toasts. */
  t: (key: string) => string;
  /**
   * Fas 2d: Free-tier får inte ingå i coop-workspaces alls. Detta är
   * klient-gate; server-side edge-fn (coop-invite-collaborator) gör
   * dubbel-check. Skicka in `'free'` om user är Free, annars valfri
   * sträng. Hooken aborterar share-link-load om tier === 'free'.
   */
  tier?: string;
}

export interface UseCoopWorkspaceResult {
  /** True while the share link (?workspace=…&mode=coop) is active. */
  coopMode: boolean;
  /** Workflow id from the URL (may be null). */
  coopWorkspaceId: string | null;
  /** Realtime channel lifecycle + presence (forwarded to the TopBar badge). */
  realtime: ReturnType<typeof useRealtimeSync>;
  /**
   * Q-101: true medan co-op-workflow fetch pågår. Används av canvas för
   * att visa skeleton-loader istället för blank canvas (2-3s av silence
   * på mobil/slow-net orsakar user att tro appen har kraschat).
   */
  isCoopLoading: boolean;
}

/**
 * Bundles the "?workspace=…&mode=coop" behaviours:
 *   1. Parse the URL once on mount.
 *   2. When authenticated, load the workflow via RLS.
 *   3. Activate the Supabase Realtime broadcast channel.
 *   4. Push a delta whenever blocks/connectors/groups change.
 *
 * Extracted from Index.tsx to keep the page component thin and to make
 * the co-op flow testable in isolation (mock loadCoopWorkflow + channel).
 */
export function useCoopWorkspace(deps: UseCoopWorkspaceDeps): UseCoopWorkspaceResult {
  const {
    user, isAuthenticated, currentDocumentId, blocks, connectors, groups,
    getActiveSheetState, loadState, resetLayerPath, setCurrentDocumentId,
    loadCoopWorkflow, t, tier,
  } = deps;

  const [searchParams] = useSearchParams();
  const coopWorkspaceId = searchParams.get('workspace');
  const coopMode = searchParams.get('mode') === 'coop';
  // Q-102: share-link kan bära `sharedBy` + `name` så toast:en blir
  // personlig ("Anna delade «Onboarding-flow» med dig").
  const sharedByName = searchParams.get('sharedBy');
  const sharedWorkspaceName = searchParams.get('name');

  // Q-101: spinner-state medan share-link-join laddar workflow.
  const [isCoopLoading, setIsCoopLoading] = useState(false);
  const coopLoadedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!coopMode || !coopWorkspaceId || !isAuthenticated) return;
    if (coopLoadedRef.current === coopWorkspaceId) return;
    // Fas 2d: Free-tier blockeras från coop. Visa toast och returnera
    // utan att markera coopLoadedRef (så user kan upgrade:a och försöka
    // igen utan att refresha sidan).
    if (tier === 'free') {
      toast({
        title: t('coopFreeBlockedTitle') ?? 'Free-plan kan inte ingå i coop',
        description: t('coopFreeBlockedHint') ?? 'Uppgradera till Advanced eller högre för att samarbeta i workspaces.',
        variant: 'destructive',
      });
      return;
    }
    coopLoadedRef.current = coopWorkspaceId;
    setIsCoopLoading(true);
    (async () => {
      try {
        const state = await loadCoopWorkflow(coopWorkspaceId);
        if (state) {
          loadState(state);
          setCurrentDocumentId(coopWorkspaceId);
          resetLayerPath();
          // Q-102: personalized toast när share-link har sharedBy+name
          const personalizedDescription =
            sharedByName && sharedWorkspaceName
              ? `${sharedByName} delade «${sharedWorkspaceName}» med dig`
              : sharedByName
                ? `Delat av ${sharedByName}`
                : sharedWorkspaceName
                  ? `Du är nu med i «${sharedWorkspaceName}»`
                  : undefined;
          toast({ title: t('coopJoined'), description: personalizedDescription });
        } else {
          toast({
            title: t('coopAccessDenied'),
            description: t('coopAccessDeniedHint'),
            variant: 'destructive',
          });
        }
      } catch {
        toast({ title: t('coopLoadFailed'), variant: 'destructive' });
      } finally {
        setIsCoopLoading(false);
      }
    })();
  }, [coopMode, coopWorkspaceId, isAuthenticated, loadCoopWorkflow, loadState, resetLayerPath, setCurrentDocumentId, t, sharedByName, sharedWorkspaceName, tier]);

  const realtime = useRealtimeSync({
    documentId: coopMode && isAuthenticated ? currentDocumentId : null,
    userId: user?.id ?? null,
    getState: getActiveSheetState,
    loadState,
  });

  useEffect(() => {
    if (!realtime.isSyncing) return;
    realtime.pushUpdate();
  }, [blocks, connectors, groups, realtime]);

  return { coopMode, coopWorkspaceId, realtime, isCoopLoading };
}
